import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import api from "@/lib/api";

const DEBOUNCE_MS = 400;

/** Chave ESTÁVEL de uma seleção: mesma seleção ⇒ mesma string, em qualquer ordem. */
export function selectionKey(ids) {
  return Array.from(ids || []).sort().join(",");
}

function isCanceled(err) {
  return (
    axios.isCancel?.(err) ||
    err?.code === "ERR_CANCELED" ||
    err?.name === "CanceledError" ||
    err?.name === "AbortError"
  );
}

/**
 * Re-cotação do que está SELECIONADO (item 14a).
 *
 * A tela cota o conjunto elegível inteiro, mas o colaborador quase sempre
 * seleciona um subconjunto: sem cotar a seleção, o valor por linha vinha de
 * rateio do agregado — o mesmo tipo de erro de base que o round 3 fechou no
 * backend, só que na exibição. Aqui a seleção é cotada no
 * `POST /anticipations/preview` (que passou a devolver `receivables_map`).
 *
 * `baseVersion` é a versão da LISTA de recebíveis: um `GET /receivables`
 * (refresh manual ou evento SSE `receivables_updated`) pode remover ou bloquear
 * uma linha SEM que a seleção mude, e a cotação anterior passaria a descrever
 * recebíveis que já não existem. Versão nova ⇒ recotação, e a cotação antiga
 * deixa de ser fresca (o consumidor compara `quote.baseVersion`).
 *
 * Contrato: FAIL-OPEN. Erro ou requisição em voo NUNCA bloqueiam a seleção nem
 * o botão — no máximo marcam o número como aproximado. O consumidor só trata a
 * cotação como firme quando `quote.ids` E `quote.baseVersion` batem com o
 * estado corrente.
 */
export function useSelectionQuote(selectedIds, baseVersion = 0) {
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState(false);

  const timerRef = useRef(null);
  const abortRef = useRef(null);
  const seqRef = useRef(0);
  // "já avisei nesta seleção" — sem isto cada clique durante uma queda do
  // backend empilharia um toast. O reset é por SELEÇÃO (não por sessão): num
  // outage sustentado o colaborador ainda é avisado a cada seleção nova.
  const warnedRef = useRef(false);

  const key = selectionKey(selectedIds);
  const version = baseVersion ?? 0;

  /**
   * Dispara a cotação AGORA: cancela o debounce pendente e a requisição em voo
   * usando o MESMO abort/seq do fluxo normal (nada de POST paralelo). Devolve a
   * cotação ou `null` (cancelada/erro) — nunca rejeita.
   */
  const runQuote = useCallback(() => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    if (!key) return Promise.resolve(null);
    if (abortRef.current) { abortRef.current.abort(); abortRef.current = null; }

    const controller = new AbortController();
    abortRef.current = controller;
    const seq = (seqRef.current += 1);
    setQuoteLoading(true);

    return api
      .post("/anticipations/preview", { receivable_ids: key.split(",") },
            { signal: controller.signal })
      .then((res) => {
        // Só a ÚLTIMA requisição disparada vale — para o ESTADO (resposta
        // atrasada de seleção antiga apagaria o "recalculando" da corrente) e
        // também para o VALOR DEVOLVIDO: quem esperou esta promise
        // (`quoteNow`) receberia a cotação de OUTRA seleção/versão e a
        // exibiria como resumo confirmável. Superada ⇒ `null`, nunca stale.
        if (seq !== seqRef.current) return null;
        // `ids`/`baseVersion` por ÚLTIMO: o payload nunca os sobrescreve.
        const fresh = { ...res.data, ids: key, baseVersion: version };
        setQuote(fresh);
        setQuoteError(false);
        setQuoteLoading(false);
        warnedRef.current = false;
        return fresh;
      })
      .catch((err) => {
        if (isCanceled(err) || seq !== seqRef.current) return null;
        // Fail-open não é fail-silent: o toast fala "aproximado" ao
        // colaborador, o console guarda a causa raiz para quem for depurar.
        console.error("[useSelectionQuote] falha ao recotar a seleção", err);
        setQuoteLoading(false);
        setQuoteError(true);
        if (!warnedRef.current) {
          warnedRef.current = true;
          toast.error("Não foi possível recotar a seleção — valor aproximado.");
        }
        return null;
      });
  }, [key, version]);

  useEffect(() => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    if (abortRef.current) { abortRef.current.abort(); abortRef.current = null; }

    if (!key) {
      // Seleção vazia: nada a cotar, e nada pendente pode voltar depois.
      seqRef.current += 1;
      warnedRef.current = false;
      setQuote(null);
      setQuoteError(false);
      setQuoteLoading(false);
      return undefined;
    }

    // Ciclo novo (seleção ou base): pode avisar de novo, e o erro do ciclo
    // ANTERIOR morre junto — mantê-lo faria a cotação nova nascer marcada como
    // aproximada mesmo antes de a requisição sair.
    warnedRef.current = false;
    setQuoteError(false);
    setQuoteLoading(true);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      runQuote();
    }, DEBOUNCE_MS);

    return () => {
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
      if (abortRef.current) { abortRef.current.abort(); abortRef.current = null; }
      // Invalidação LÓGICA além do abort: uma resposta já entregue pela rede
      // (ou um abort que chega tarde demais) ainda cairia no `then` com o `seq`
      // válido e escreveria a cotação de uma seleção/versão que não existe
      // mais. Incrementando aqui, o guard do `then` a rejeita de fato — e quem
      // esperava a promise recebe `null` em vez de dado stale.
      seqRef.current += 1;
    };
  }, [key, version, runQuote]);

  return { quote, quoteLoading, quoteError, quoteNow: runQuote };
}
